import React, { useCallback, useEffect, useState } from 'react';
import { Button, DatePicker, Input, Select, Space, Table, Tabs, Tag, Typography } from 'antd';
import { CloudUploadOutlined, SearchOutlined } from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  getOrderCallbackLogs,
  getTrackedOrders,
} from '../../api/orderTracking';
import { useAuth } from '../../store/authContext';
import ChannelOrderImportDialog from './ChannelOrderImportDialog';

const { Paragraph, Text } = Typography;

const platformOptions = [
  { value: 'TAOBAO', label: '淘宝' },
  { value: 'JD', label: '京东' },
];

const orderStatusMap = {
  CREATED: { label: '已创建', color: 'default' },
  PAID: { label: '已付款', color: 'blue' },
  CONFIRMED: { label: '已确认', color: 'green' },
  COMPLETED: { label: '已完成', color: 'green' },
  REFUNDED: { label: '已退款', color: 'red' },
};

const rebateStatusMap = {
  PENDING: { label: '待结算', color: 'orange' },
  CONFIRMED: { label: '已确认', color: 'blue' },
  SETTLED: { label: '已结算', color: 'green' },
  REFUNDED: { label: '已退款', color: 'red' },
};

const attributionSourceMap = {
  TAOBAO_SYNC: '淘宝可信归因',
  UNATTRIBUTED: '未归因',
};

const safeHttpsUrl = (value) => {
  if (!value) {
    return null;
  }
  try {
    const parsed = new URL(value);
    return parsed.protocol === 'https:' ? parsed.href : null;
  } catch {
    return null;
  }
};

const actionMap = {
  tracked: { label: '仅跟踪', color: 'default' },
  created: { label: '创建返利', color: 'blue' },
  updated: { label: '更新订单', color: 'processing' },
  confirmed: { label: '确认返利', color: 'green' },
  refunded: { label: '退款回滚', color: 'red' },
  ignored: { label: '忽略重复', color: 'default' },
};

const processingResultMap = {
  SUCCESS: { label: '成功', color: 'green' },
  ERROR: { label: '失败', color: 'red' },
};

const buildSearchParams = (activeTab, orderFilters, logFilters) => {
  const params = { tab: activeTab };
  if (orderFilters.orderId) {
    params.orderId = orderFilters.orderId;
  }
  if (orderFilters.userId) {
    params.userId = orderFilters.userId;
  }
  if (orderFilters.platform) {
    params.platform = orderFilters.platform;
  }
  if (orderFilters.orderStatus) {
    params.orderStatus = orderFilters.orderStatus;
  }
  if (orderFilters.attributionSource) {
    params.attributionSource = orderFilters.attributionSource;
  }
  if (orderFilters.sourceSettled) {
    params.sourceSettled = orderFilters.sourceSettled;
  }
  if (orderFilters.commissionState) {
    params.commissionState = orderFilters.commissionState;
  }
  if (orderFilters.orderTimeFrom) {
    params.orderTimeFrom = orderFilters.orderTimeFrom;
  }
  if (orderFilters.orderTimeTo) {
    params.orderTimeTo = orderFilters.orderTimeTo;
  }
  if (logFilters.orderId) {
    params.logOrderId = logFilters.orderId;
  }
  if (logFilters.platform) {
    params.logPlatform = logFilters.platform;
  }
  if (logFilters.normalizedStatus) {
    params.normalizedStatus = logFilters.normalizedStatus;
  }
  if (logFilters.action) {
    params.action = logFilters.action;
  }
  if (logFilters.processingResult) {
    params.processingResult = logFilters.processingResult;
  }
  if (logFilters.logTimeFrom) {
    params.logTimeFrom = logFilters.logTimeFrom;
  }
  if (logFilters.logTimeTo) {
    params.logTimeTo = logFilters.logTimeTo;
  }
  return params;
};

const OrderTracking = () => {
  const navigate = useNavigate();
  const { admin } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || 'orders';
  const initialOrderFilters = {
    orderId: searchParams.get('orderId') || '',
    userId: searchParams.get('userId') || '',
    platform: searchParams.get('platform') || '',
    orderStatus: searchParams.get('orderStatus') || '',
    attributionSource: searchParams.get('attributionSource') || '',
    sourceSettled: searchParams.get('sourceSettled') || '',
    commissionState: searchParams.get('commissionState') || '',
    orderTimeFrom: searchParams.get('orderTimeFrom') || '',
    orderTimeTo: searchParams.get('orderTimeTo') || '',
  };
  const initialLogFilters = {
    orderId: searchParams.get('logOrderId') || '',
    platform: searchParams.get('logPlatform') || '',
    normalizedStatus: searchParams.get('normalizedStatus') || '',
    action: searchParams.get('action') || '',
    processingResult: searchParams.get('processingResult') || '',
    logTimeFrom: searchParams.get('logTimeFrom') || '',
    logTimeTo: searchParams.get('logTimeTo') || '',
  };

  const [activeTab, setActiveTab] = useState(initialTab);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [logsLoading, setLogsLoading] = useState(false);
  const [orders, setOrders] = useState([]);
  const [logs, setLogs] = useState([]);
  const [orderPagination, setOrderPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [logPagination, setLogPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [orderFilters, setOrderFilters] = useState(initialOrderFilters);
  const [appliedOrderFilters, setAppliedOrderFilters] = useState(initialOrderFilters);
  const [logFilters, setLogFilters] = useState(initialLogFilters);
  const [appliedLogFilters, setAppliedLogFilters] = useState(initialLogFilters);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const orderCurrentPage = orderPagination.current;
  const orderPageSize = orderPagination.pageSize;
  const logCurrentPage = logPagination.current;
  const logPageSize = logPagination.pageSize;

  const fetchOrders = useCallback(async () => {
    setOrdersLoading(true);
    try {
      const result = await getTrackedOrders({
        page: orderCurrentPage,
        size: orderPageSize,
        orderId: appliedOrderFilters.orderId || undefined,
        userId: appliedOrderFilters.userId || undefined,
        platform: appliedOrderFilters.platform || undefined,
        orderStatus: appliedOrderFilters.orderStatus || undefined,
        attributionSource: appliedOrderFilters.attributionSource || undefined,
        sourceSettled: appliedOrderFilters.sourceSettled || undefined,
        commissionState: appliedOrderFilters.commissionState || undefined,
        orderTimeFrom: appliedOrderFilters.orderTimeFrom || undefined,
        orderTimeTo: appliedOrderFilters.orderTimeTo || undefined,
      });
      setOrders(result.list || []);
      setOrderPagination((prev) => ({ ...prev, total: result.total || 0 }));
    } catch (error) {
      console.error('Failed to fetch tracked orders:', error);
    } finally {
      setOrdersLoading(false);
    }
  }, [orderCurrentPage, orderPageSize, appliedOrderFilters]);

  const fetchLogs = useCallback(async () => {
    setLogsLoading(true);
    try {
      const result = await getOrderCallbackLogs({
        page: logCurrentPage,
        size: logPageSize,
        orderId: appliedLogFilters.orderId || undefined,
        platform: appliedLogFilters.platform || undefined,
        normalizedStatus: appliedLogFilters.normalizedStatus || undefined,
        action: appliedLogFilters.action || undefined,
        processingResult: appliedLogFilters.processingResult || undefined,
        logTimeFrom: appliedLogFilters.logTimeFrom || undefined,
        logTimeTo: appliedLogFilters.logTimeTo || undefined,
      });
      setLogs(result.list || []);
      setLogPagination((prev) => ({ ...prev, total: result.total || 0 }));
    } catch (error) {
      console.error('Failed to fetch callback logs:', error);
    } finally {
      setLogsLoading(false);
    }
  }, [logCurrentPage, logPageSize, appliedLogFilters]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const refreshAfterImport = useCallback(async () => {
    await Promise.all([fetchOrders(), fetchLogs()]);
  }, [fetchLogs, fetchOrders]);

  const syncParams = (nextTab, nextOrderFilters, nextLogFilters) => {
    setSearchParams(buildSearchParams(nextTab, nextOrderFilters, nextLogFilters));
  };

  const handleOrderSearch = () => {
    const nextFilters = { ...orderFilters };
    setAppliedOrderFilters(nextFilters);
    setOrderPagination((prev) => ({ ...prev, current: 1 }));
    syncParams(activeTab, nextFilters, appliedLogFilters);
  };

  const handleLogSearch = () => {
    const nextFilters = { ...logFilters };
    setAppliedLogFilters(nextFilters);
    setLogPagination((prev) => ({ ...prev, current: 1 }));
    syncParams(activeTab, appliedOrderFilters, nextFilters);
  };

  const handleTabChange = (key) => {
    setActiveTab(key);
    syncParams(key, appliedOrderFilters, appliedLogFilters);
  };

  const jumpToOrder = (orderId) => {
    const nextOrderFilters = { ...appliedOrderFilters, orderId };
    const nextLogFilters = { ...appliedLogFilters, orderId };
    setActiveTab('orders');
    setOrderFilters(nextOrderFilters);
    setAppliedOrderFilters(nextOrderFilters);
    setLogFilters(nextLogFilters);
    setAppliedLogFilters(nextLogFilters);
    setOrderPagination((prev) => ({ ...prev, current: 1 }));
    setLogPagination((prev) => ({ ...prev, current: 1 }));
    syncParams('orders', nextOrderFilters, nextLogFilters);
  };

  const orderColumns = [
    {
      title: '订单',
      key: 'order',
      width: 210,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text>{record.orderId || '-'}</Text>
          <Text type="secondary">父单: {record.sourceParentOrderId || '-'}</Text>
        </Space>
      ),
    },
    {
      title: '用户',
      key: 'user',
      width: 180,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          {record.userId ? (
            <Button type="link" style={{ padding: 0 }} onClick={() => navigate(`/users?userId=${record.userId}`)}>
              {record.userNickname || `用户 ${record.userId}`}
            </Button>
          ) : (
            <Tag>未归因</Tag>
          )}
          <Text type="secondary">ID: {record.userId || '-'}</Text>
          <Text type="secondary">
            {attributionSourceMap[record.attributionSource] || record.attributionSource || '归因未知'}
          </Text>
        </Space>
      ),
    },
    {
      title: '来源',
      key: 'source',
      width: 120,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Tag>{record.platform || '-'}</Tag>
          <Text type="secondary">{record.sourceClient || '-'}</Text>
        </Space>
      ),
    },
    {
      title: '商品',
      key: 'product',
      width: 260,
      render: (_, record) => {
        const productImageUrl = safeHttpsUrl(record.productImage);
        return (
          <Space direction="vertical" size={0}>
            <span>{record.productTitle || '-'}</span>
            <Text type="secondary">商品ID: {record.productId || '-'}</Text>
            {productImageUrl ? (
              <a href={productImageUrl} target="_blank" rel="noopener noreferrer">查看商品图片</a>
            ) : null}
          </Space>
        );
      },
    },
    {
      title: '订单总额',
      key: 'amount',
      width: 140,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <span>{record.orderAmount == null ? '—' : `¥${Number(record.orderAmount).toFixed(2)}`}</span>
          <Text type="secondary">
            结算 {record.settledAmount == null ? '-' : `¥${Number(record.settledAmount).toFixed(2)}`}
          </Text>
        </Space>
      ),
    },
    {
      title: '佣金',
      dataIndex: 'commissionAmount',
      width: 120,
      render: (value) => `¥${Number(value || 0).toFixed(2)}`,
    },
    {
      title: '订单状态',
      dataIndex: 'orderStatus',
      width: 120,
      render: (value, record) => {
        const item = orderStatusMap[value] || { label: value, color: 'default' };
        return (
          <Space direction="vertical" size={0}>
            <Tag color={item.color}>{item.label}</Tag>
            <Text type="secondary">最终结算: {Number(record.sourceSettled) === 1 ? '是' : '否'}</Text>
          </Space>
        );
      },
    },
    {
      title: '来源时间',
      dataIndex: 'sourceOrderTime',
      width: 160,
      render: (value) => value ? dayjs(value).format('YYYY-MM-DD HH:mm') : '-',
    },
    {
      title: '回流',
      key: 'callbacks',
      width: 180,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <span>次数 {record.callbackCount || 0}</span>
          <Text type="secondary">
            首次 {record.firstCallbackAt ? dayjs(record.firstCallbackAt).format('YYYY-MM-DD HH:mm') : '-'}
          </Text>
          <Text type="secondary">
            最近 {record.lastCallbackAt ? dayjs(record.lastCallbackAt).format('YYYY-MM-DD HH:mm') : '-'}
          </Text>
        </Space>
      ),
    },
    {
      title: '返利',
      key: 'rebate',
      width: 150,
      render: (_, record) => {
        if (!record.rebateStatus) {
          return <Tag>未创建</Tag>;
        }
        const item = rebateStatusMap[record.rebateStatus]
          || { label: record.rebateStatus, color: 'default' };
        return (
          <Space direction="vertical" size={0}>
            <Tag color={item.color}>{item.label}</Tag>
            <Text type="secondary">{record.rebateType || '-'}</Text>
            <span>
              {record.actualRebateAmount == null
                ? '-'
                : `¥${Number(record.actualRebateAmount).toFixed(2)}`}
            </span>
          </Space>
        );
      },
    },
    {
      title: '返利比例',
      key: 'rebateRatio',
      width: 130,
      render: (_, record) => {
        const total = Number(record.orderAmount);
        const rebate = Number(record.actualRebateAmount);
        if (!record.rebateStatus || record.actualRebateAmount == null
            || !Number.isFinite(total) || total <= 0
            || !Number.isFinite(rebate) || rebate < 0) return '—';
        return `${(rebate / total * 100).toFixed(2)}%`;
      },
    },
  ];

  const logColumns = [
    {
      title: '订单号',
      dataIndex: 'orderId',
      width: 180,
      render: (value) => (
        <Button type="link" style={{ padding: 0 }} onClick={() => jumpToOrder(value)}>
          {value}
        </Button>
      ),
    },
    {
      title: '平台',
      dataIndex: 'platform',
      width: 100,
      render: (value) => <Tag>{value || '-'}</Tag>,
    },
    {
      title: '状态',
      key: 'status',
      width: 170,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <span>原始: {record.rawStatus || '-'}</span>
          <span>归一: {record.normalizedStatus || '-'}</span>
        </Space>
      ),
    },
    {
      title: '动作',
      dataIndex: 'action',
      width: 120,
      render: (value) => {
        const item = actionMap[value] || { label: value, color: 'default' };
        return <Tag color={item.color}>{item.label}</Tag>;
      },
    },
    {
      title: '处理结果',
      dataIndex: 'processingResult',
      width: 110,
      render: (value) => {
        const item = processingResultMap[value] || { label: value, color: 'default' };
        return <Tag color={item.color}>{item.label}</Tag>;
      },
    },
    {
      title: '错误信息',
      dataIndex: 'errorMessage',
      width: 180,
      render: (value) => value || '-',
    },
    {
      title: '请求快照',
      dataIndex: 'requestSnapshot',
      width: 320,
      render: (value) => (
        <Paragraph
          copyable={!!value}
          ellipsis={{ rows: 2, expandable: true, symbol: '展开' }}
          style={{ marginBottom: 0, maxWidth: 280 }}
        >
          {value || '-'}
        </Paragraph>
      ),
    },
    {
      title: '回调时间',
      dataIndex: 'createdAt',
      width: 160,
      render: (value) => value ? dayjs(value).format('YYYY-MM-DD HH:mm') : '-',
    },
  ];

  const orderDateRange = orderFilters.orderTimeFrom && orderFilters.orderTimeTo
    ? [dayjs(orderFilters.orderTimeFrom), dayjs(orderFilters.orderTimeTo)]
    : null;
  const logDateRange = logFilters.logTimeFrom && logFilters.logTimeTo
    ? [dayjs(logFilters.logTimeFrom), dayjs(logFilters.logTimeTo)]
    : null;

  const tabItems = [
    {
      key: 'orders',
      label: '订单跟踪',
      children: (
        <>
          <Space style={{ marginBottom: 16 }} wrap>
            <Input
              placeholder="订单号"
              value={orderFilters.orderId}
              onChange={(e) => setOrderFilters((prev) => ({ ...prev, orderId: e.target.value }))}
              onPressEnter={handleOrderSearch}
              style={{ width: 200 }}
            />
            <Input
              placeholder="用户ID"
              value={orderFilters.userId}
              onChange={(e) => setOrderFilters((prev) => ({ ...prev, userId: e.target.value }))}
              onPressEnter={handleOrderSearch}
              style={{ width: 140 }}
            />
            <Select
              placeholder="平台"
              allowClear
              style={{ width: 140 }}
              value={orderFilters.platform || undefined}
              onChange={(value) => setOrderFilters((prev) => ({ ...prev, platform: value || '' }))}
              options={platformOptions}
            />
            <Select
              placeholder="订单状态"
              allowClear
              style={{ width: 150 }}
              value={orderFilters.orderStatus || undefined}
              onChange={(value) => setOrderFilters((prev) => ({ ...prev, orderStatus: value || '' }))}
              options={[
                { value: 'WAITING_CONFIRMATION', label: '待确认（已创建/已付款）' },
                { value: 'CREATED', label: '已创建' },
                { value: 'PAID', label: '已付款' },
                { value: 'CONFIRMED', label: '已确认' },
                { value: 'COMPLETED', label: '已完成' },
                { value: 'REFUNDED', label: '已退款' },
              ]}
            />
            <Select
              placeholder="归因状态"
              allowClear
              style={{ width: 140 }}
              value={orderFilters.attributionSource || undefined}
              onChange={(value) => setOrderFilters((prev) => ({ ...prev, attributionSource: value || '' }))}
              options={[
                { value: 'ATTRIBUTED', label: '已归因' },
                { value: 'UNATTRIBUTED', label: '未归因' },
              ]}
            />
            <Select
              placeholder="结算状态"
              allowClear
              style={{ width: 140 }}
              value={orderFilters.sourceSettled || undefined}
              onChange={(value) => setOrderFilters((prev) => ({ ...prev, sourceSettled: value || '' }))}
              options={[
                { value: '1', label: '渠道已结算' },
                { value: '0', label: '渠道未结算' },
              ]}
            />
            <Select
              placeholder="佣金数据"
              allowClear
              style={{ width: 140 }}
              value={orderFilters.commissionState || undefined}
              onChange={(value) => setOrderFilters((prev) => ({ ...prev, commissionState: value || '' }))}
              options={[{ value: 'MISSING', label: '佣金缺失' }]}
            />
            <DatePicker.RangePicker
              value={orderDateRange}
              format="YYYY-MM-DD"
              placeholder={['订单开始日期', '订单结束日期']}
              onChange={(_, dateStrings) => setOrderFilters((prev) => ({
                ...prev,
                orderTimeFrom: dateStrings?.[0] || '',
                orderTimeTo: dateStrings?.[1] || '',
              }))}
            />
            <Button type="primary" icon={<SearchOutlined />} onClick={handleOrderSearch}>
              搜索订单
            </Button>
          </Space>

          <Table
            columns={orderColumns}
            dataSource={orders}
            rowKey="id"
            loading={ordersLoading}
            scroll={{ x: 1830 }}
            pagination={{
              ...orderPagination,
              showSizeChanger: true,
              showTotal: (total) => `共 ${total} 条`,
              onChange: (page, pageSize) => setOrderPagination({ current: page, pageSize, total: orderPagination.total }),
            }}
          />
        </>
      ),
    },
    {
      key: 'logs',
      label: '回调日志',
      children: (
        <>
          <Space style={{ marginBottom: 16 }} wrap>
            <Input
              placeholder="订单号"
              value={logFilters.orderId}
              onChange={(e) => setLogFilters((prev) => ({ ...prev, orderId: e.target.value }))}
              onPressEnter={handleLogSearch}
              style={{ width: 200 }}
            />
            <Select
              placeholder="平台"
              allowClear
              style={{ width: 140 }}
              value={logFilters.platform || undefined}
              onChange={(value) => setLogFilters((prev) => ({ ...prev, platform: value || '' }))}
              options={platformOptions}
            />
            <Select
              placeholder="归一状态"
              allowClear
              style={{ width: 150 }}
              value={logFilters.normalizedStatus || undefined}
              onChange={(value) => setLogFilters((prev) => ({ ...prev, normalizedStatus: value || '' }))}
              options={[
                { value: 'CREATED', label: '已创建' },
                { value: 'PAID', label: '已付款' },
                { value: 'CONFIRMED', label: '已确认' },
                { value: 'COMPLETED', label: '已完成' },
                { value: 'REFUNDED', label: '已退款' },
              ]}
            />
            <Select
              placeholder="处理动作"
              allowClear
              style={{ width: 150 }}
              value={logFilters.action || undefined}
              onChange={(value) => setLogFilters((prev) => ({ ...prev, action: value || '' }))}
              options={[
                { value: 'tracked', label: '仅跟踪' },
                { value: 'created', label: '创建返利' },
                { value: 'updated', label: '更新订单' },
                { value: 'confirmed', label: '确认返利' },
                { value: 'refunded', label: '退款回滚' },
                { value: 'ignored', label: '忽略重复' },
              ]}
            />
            <Select
              placeholder="处理结果"
              allowClear
              style={{ width: 140 }}
              value={logFilters.processingResult || undefined}
              onChange={(value) => setLogFilters((prev) => ({ ...prev, processingResult: value || '' }))}
              options={[
                { value: 'SUCCESS', label: '成功' },
                { value: 'FAILED', label: '非成功' },
                { value: 'ERROR', label: '失败' },
              ]}
            />
            <DatePicker.RangePicker
              value={logDateRange}
              format="YYYY-MM-DD"
              placeholder={['回调开始日期', '回调结束日期']}
              onChange={(_, dateStrings) => setLogFilters((prev) => ({
                ...prev,
                logTimeFrom: dateStrings?.[0] || '',
                logTimeTo: dateStrings?.[1] || '',
              }))}
            />
            <Button type="primary" icon={<SearchOutlined />} onClick={handleLogSearch}>
              搜索日志
            </Button>
          </Space>

          <Table
            columns={logColumns}
            dataSource={logs}
            rowKey="id"
            loading={logsLoading}
            scroll={{ x: 1650 }}
            pagination={{
              ...logPagination,
              showSizeChanger: true,
              showTotal: (total) => `共 ${total} 条`,
              onChange: (page, pageSize) => setLogPagination({ current: page, pageSize, total: logPagination.total }),
            }}
          />
        </>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>订单追踪</h2>
        {admin?.role === 'SUPER_ADMIN' ? (
          <Button
            type="primary"
            icon={<CloudUploadOutlined />}
            onClick={() => setImportDialogOpen(true)}
          >
            导入渠道订单
          </Button>
        ) : null}
      </div>

      {admin?.role === 'SUPER_ADMIN' ? (
        <ChannelOrderImportDialog
          open={importDialogOpen}
          onClose={() => setImportDialogOpen(false)}
          onImported={refreshAfterImport}
        />
      ) : null}

      <Tabs activeKey={activeTab} items={tabItems} onChange={handleTabChange} />
    </div>
  );
};

export default OrderTracking;
